import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AppForm } from '../../../../../../common/forms/app.form';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite
} from '../../../../../../openapi';
import { environment } from '../../../../../../../environments/environment';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';

export type DeviceType = 'Urządzenia obce' | 'Reklama' | '';

@Injectable()
export class ApplicationDataForm extends AppForm {

  public deviceTypes: DeviceType[] = [
    'Urządzenia obce'
  ];

  public exitTypes: string[] = [
    'Zwykły',
    'Techniczny',
    'Awaryjny'
  ];

  public exitDirections: string[] = [
    'Jednokierunkowy',
    'Dwukierunkowy'
  ];

  constructor() {
    super({
      office: new FormControl({ value: environment.applicationSubmittedTo, disabled: true }, { nonNullable: true }),
      documentReceiptMethod: new FormControl('', { validators: [Validators.required], nonNullable: true })
    });
  }

  public createExitDirectionControl(): void {
    this.addControl(
      'exitDirection',
      new FormControl('',
        {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(255)]
        }
      )
    );
  }

  public get exitDirection(): FormControl {
    return this.get('exitDirection') as FormControl;
  }

  public createExitTypeControl(): void {
    this.addControl(
      'exitType',
      new FormControl('',
        {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(255)]
        }
      )
    );
  }

  public get exitType(): FormControl {
    return this.get('exitType') as FormControl;
  }

  public get objectName(): FormControl | null {
    return this.get('objectName') as FormControl | null;
  }

  public createObjectNameControl(): void {
    this.addControl('objectName', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
  }

  public get roadTrafficImpact(): FormControl | null {
    return this.get('roadTrafficImpact') as FormControl | null;
  }

  public createRoadTrafficImpactControl(): void {
    this.addControl('roadTrafficImpact', new FormControl('Nie', Validators.required));
  }

  public get agreementNumber(): FormControl | null {
    return this.get('agreementNumber') as FormControl | null;
  }

  public get agreementDate(): FormControl | null {
    return this.get('agreementDate') as FormControl | null;
  }

  public createAgreementControls(): void {
    this.addControl('agreementNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    this.addControl('agreementDate', new FormControl(null, [Validators.required, CustomValidator.maxDate(new Date())]));
  }

  public get documentReceiptMethod(): FormControl {
    return this.get('documentReceiptMethod') as FormControl;
  }

  public get ePuapAddress(): FormControl | null {
    return this.get('ePuapAddress') as FormControl | null;
  }

  public get eDeliveryAddress(): FormControl | null {
    return this.get('eDeliveryAddress') as FormControl | null;
  }

  public documentReceiptMethodChange(v: string): void {
    if (v === '4d536cdd-dc3a-42f9-bf89-342e5cc0c885') {
      this.addControl(
        'ePuapAddress',
        new FormControl('', [Validators.required, Validators.maxLength(255)])
      );
    } else {
      this.removeControl('ePuapAddress');
    }
    if (v === '6bef93e4-f125-49f2-ab12-11ddc5e1e8a2') {
      this.addControl('eDeliveryAddress', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('eDeliveryAddress');
    }
  }

  public createApplicationSubjectControl(): void {
    this.addControl('applicationSubject', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
      nonNullable: true
    }));
  }

  public get reconstructionDescription(): FormControl | null {
    return this.get('reconstructionDescription') as FormControl | null;
  }

  public applicationSubjectChange(v: string): void {
    if (v === 'Przebudowy zjazdu') {
      this.addControl('reconstructionDescription', new FormControl(null, { validators: [Validators.required, Validators.maxLength(5000)] }));
    } else {
      this.removeControl('reconstructionDescription');
    }

    if (v === 'Kategorii II') {
      this.addControl('transitPeriod', new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }));
    } else {
      this.removeControl('transitPeriod');
    }
  }

  public get transitPeriod(): FormControl | null {
    return this.get('transitPeriod') as FormControl | null;
  }

  public createApplicationConcernsStreetControl(): void {
    this.addControl('applicationConcernsStreet', new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }));
  }

  public get applicationConcernsStreet(): FormControl | null {
    return this.get('applicationConcernsStreet') as FormControl | null;
  }

  public get applicationSubject(): FormControl | null {
    return this.get('applicationSubject') as FormControl | null;
  }

  public showResponsiblePersonControls(): boolean {
    return !!this.responsiblePersonPhone && !!this.responsiblePersonLastName && !!this.responsiblePersonFirstName && !!this.responsiblePersonEmail;
  }

  private get responsiblePersonFirstName(): FormControl | null {
    return this.get('responsiblePersonFirstName') as FormControl | null;
  }

  private get responsiblePersonLastName(): FormControl | null {
    return this.get('responsiblePersonLastName') as FormControl | null;
  }

  private get responsiblePersonPhone(): FormControl | null {
    return this.get('responsiblePersonPhone') as FormControl | null;
  }

  private get responsiblePersonEmail(): FormControl | null {
    return this.get('responsiblePersonEmail') as FormControl | null;
  }

  public createResponsiblePersonControls(): void {
    this.addControl('responsiblePersonFirstName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonLastName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonPhone', new FormControl(''));
    this.addControl('responsiblePersonEmail', new FormControl('', [Validators.email, Validators.maxLength(255)]));
  }

  private get managerFirstName(): FormControl | null {
    return this.get('managerFirstName') as FormControl | null;
  }

  private get managerLastName(): FormControl | null {
    return this.get('managerLastName') as FormControl | null;
  }

  private get managerPhone(): FormControl | null {
    return this.get('managerPhone') as FormControl | null;
  }

  private get managerEmail(): FormControl | null {
    return this.get('managerEmail') as FormControl | null;
  }

  public showManagerControls(): boolean {
    return !!this.managerFirstName && !!this.managerLastName && !!this.managerPhone && !!this.managerEmail;
  }

  public createManagerControls(): void {
    this.addControl('managerFirstName', new FormControl('', [Validators.maxLength(255)]));
    this.addControl('managerLastName', new FormControl('', [Validators.maxLength(255)]));
    this.addControl('managerPhone', new FormControl('', [Validators.maxLength(255)]));
    this.addControl('managerEmail', new FormControl('', [Validators.maxLength(255)]));
  }

  public get notes(): FormControl | null {
    return this.get('notes') as FormControl | null;
  }

  public createNotesControl(): void {
    this.addControl('notes', new FormControl('', [Validators.maxLength(5000)]));
  }

  public createDeviceTypeControl(): void {
    this.addControl('deviceType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
    if (this.deviceTypes.length === 1) {
      this.deviceType?.disable();
    }
  }

  public get deviceType(): FormControl | null {
    return this.get('deviceType') as FormControl | null;
  }

  public createCorrespondenceControls(): void {
    this.addControl('correspondenceFirstName', new FormControl(null, [Validators.maxLength(255)]));
    this.addControl('correspondenceLastName', new FormControl(null, [Validators.maxLength(255)]));
    this.addControl('correspondencePhone', new FormControl(null, [CustomValidator.phone(), Validators.maxLength(255)]));
  }

  public get showCorrespondenceControls(): boolean {
    return !!this.correspondencePhone
      || !!this.correspondenceLastName
      || !!this.correspondenceFirstName;
  }

  public get correspondenceFirstName(): FormControl | null {
    return this.get('correspondenceFirstName') as FormControl | null;
  }

  public get correspondenceLastName(): FormControl | null {
    return this.get('correspondenceLastName') as FormControl | null;
  }

  public get correspondencePhone(): FormControl | null {
    return this.get('correspondencePhone') as FormControl | null;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      applicationSubject: this.value?.applicationSubject || null,
      transitPeriod: this.transitPeriod?.value || null,
      reconstructionDescription: this.reconstructionDescription?.value || null,
      documentReceiptMethod: this.value.documentReceiptMethod,
      responsiblePersonFirstName: this.value?.responsiblePersonFirstName,
      responsiblePersonLastName: this.value?.responsiblePersonLastName,
      responsiblePersonPhone: this.value?.responsiblePersonPhone,
      responsiblePersonEmail: this.value?.responsiblePersonEmail,
      managerEmail: this.value?.managerEmail,
      managerPhone: this.value?.managerPhone,
      managerFirstName: this.value?.managerFirstName,
      managerLastName: this.value?.managerLastName,
      notes: this.value?.notes,
      deviceTypeDescription: this.value?.deviceTypeDescription || null,
      adContent: this.value?.adContent || null,
      ePuapAddress: this.ePuapAddress?.value || null,
      eDeliveryAddress: this.eDeliveryAddress?.value || null,
      applicationConcernsStreet: this.applicationConcernsStreet?.value || null,
      deviceType: this.deviceType?.value || null,
      agreementNumber: this.agreementNumber?.value,
      agreementDate: this.agreementDate?.value,
      objectName: this.objectName?.value || null,
      exitType: this.exitType?.value || null,
      exitDirection: this.exitDirection?.value || null,
      roadTrafficImpact: this.roadTrafficImpact?.value || null,

      correspondenceFirstName: this.value?.correspondenceFirstName,
      correspondenceLastName: this.value?.correspondenceLastName,
      correspondencePhone: this.value?.correspondencePhone
    };
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.patchValue({
      documentReceiptMethod: app?.documentReceiptMethod?.id || '',
      applicationSubject: app?.applicationSubject || '',
      responsiblePersonFirstName: app?.responsiblePersonFirstName,
      responsiblePersonLastName: app?.responsiblePersonLastName,
      responsiblePersonPhone: app?.responsiblePersonPhone,
      responsiblePersonEmail: app?.responsiblePersonEmail,
      managerEmail: app?.managerEmail,
      managerPhone: app?.managerPhone,
      managerFirstName: app?.managerFirstName,
      managerLastName: app?.managerLastName,
      notes: app?.notes,
      deviceType: app?.deviceType || (this.deviceTypes.length === 1 ? this.deviceTypes.at(0) : ''),
      legalInterest: app?.legalInterest,
      actType: app?.actType || '',
      applicationConcernsStreet: app?.applicationConcernsStreet,
      agreementNumber: app?.agreementNumber,
      agreementDate: app?.agreementDate,
      objectName: app?.objectName,
      exitType: app?.exitType || '',
      exitDirection: app?.exitDirection || '',
      roadTrafficImpact: app?.roadTrafficImpact || 'Nie',
      correspondenceFirstName: app?.correspondenceFirstName,
      correspondenceLastName: app?.correspondenceLastName,
      correspondencePhone: app?.correspondencePhone
    });

    this.applicationSubjectChange(app?.applicationSubject || '');
    this.transitPeriod?.setValue(app?.transitPeriod || '');
    this.reconstructionDescription?.setValue(app?.reconstructionDescription);

    this.documentReceiptMethodChange(app?.documentReceiptMethod?.id || '');
    this.ePuapAddress?.setValue(app?.ePuapAddress || '');
    this.eDeliveryAddress?.setValue(app?.eDeliveryAddress);
  }
}
